import { INTERNAL_DIMENSIONS, type InternalDimension } from "../types";
import { displayDimension } from "../logic/dimensions";
import type { CSSProperties } from "react";

interface RadarFigureProps {
  values: Record<InternalDimension, number>;
  max?: number;
}

const CENTER = 180;
const RADIUS = 108;

function point(index: number, radius: number) {
  const angle = -Math.PI / 2 + (Math.PI * 2 * index) / INTERNAL_DIMENSIONS.length;
  return [CENTER + Math.cos(angle) * radius, CENTER + Math.sin(angle) * radius] as const;
}

function polygon(radius: number) {
  return INTERNAL_DIMENSIONS.map((_, index) => point(index, radius).join(",")).join(" ");
}

export function RadarFigure({ values, max = 100 }: RadarFigureProps) {
  const dataPolygon = INTERNAL_DIMENSIONS.map((dimension, index) => {
    const radius = Math.max(0, Math.min(1, values[dimension] / max)) * RADIUS;
    return point(index, radius).join(",");
  }).join(" ");
  const accessibleText = INTERNAL_DIMENSIONS.map((dimension) => `${displayDimension(dimension)} ${Math.round(values[dimension])}`).join("，");

  return (
    <figure className="radar-figure">
      <svg viewBox="0 0 360 360" role="img" aria-label={`六维分布：${accessibleText}`}>
        {[1, 0.66, 0.33].map((scale) => <polygon key={scale} points={polygon(RADIUS * scale)} className="radar-grid" />)}
        {INTERNAL_DIMENSIONS.map((_, index) => {
          const [x, y] = point(index, RADIUS);
          return <line key={index} x1={CENTER} y1={CENTER} x2={x} y2={y} className="radar-axis" />;
        })}
        <polygon points={dataPolygon} className="radar-value" />
        {INTERNAL_DIMENSIONS.map((dimension, index) => {
          const radius = Math.max(0, Math.min(1, values[dimension] / max)) * RADIUS;
          const [x, y] = point(index, radius);
          return <circle key={`point-${dimension}`} cx={x} cy={y} r="2.6" className="radar-point" />;
        })}
        <circle cx={CENTER} cy={CENTER} r="20" className="radar-center" />
        <text x={CENTER} y={CENTER} textAnchor="middle" dominantBaseline="middle" className="radar-center-label">WMLS</text>
        {INTERNAL_DIMENSIONS.map((dimension, index) => {
          const [x, y] = point(index, RADIUS + 42);
          return (
            <text key={dimension} x={x} y={y} textAnchor="middle" dominantBaseline="middle" className="radar-label" style={{ "--radar-label-index": index } as CSSProperties}>
              <tspan x={x} dy="-0.35em">{displayDimension(dimension)}</tspan>
              <tspan x={x} dy="1.35em" className="radar-score">{Math.round(values[dimension])}</tspan>
            </text>
          );
        })}
      </svg>
      <figcaption className="figure-caption">六维人格印记</figcaption>
    </figure>
  );
}
